// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as BrandsAPI from 'lib/api/brands';

const GET_RECENT_BRANDS = 'branding/GET_RECENT_BRANDS';
const PREFETCH_RECENT_BRANDS = 'branding/PREFETCH';
const REVEAL_PREFETCHED = 'branding/REVEAL_PREFETCHED';
const CLEAR_USER_BRANDS = 'branding/CLEAR_USER_BRANDS';
const GET_USER_BRANDS = 'branding/GET_USER_BRANDS';
const PREFETCH_USER_BRANDS = 'branding/PREFETCH_USER_BRANDS';
const GET_TRENDING_BRANDS = 'branding/GET_TRENDING_BRANDS';
const PREFETCH_TRENDING_BRANDS = 'branding/PREFETCH_TRENDING_BRANDS';
const GET_TAG_BRANDS = 'branding/GET_TAG_BRANDS';
const PREFETCH_TAG_BRANDS = 'branding/PREFETCH_TAG_BRANDS';
const GET_CATEGORY_BRANDS = 'branding/GET_CATEGORY_BRANDS';
const PREFETCH_CATEGORY_BRANDS = 'branding/PREFETCH_CATEGORY_BRANDS';
const CLEAR_CATEGORY_BRANDS = 'branding/CLEAR_CATEGORY_BRANDS';
const CLEAR_TAG_BRANDS = 'branding/CLEAR_TAG_BRANDS';
const GET_TEMP_BRANDS = 'branding/GET_TEMP_BRANDS';
const PREFETCH_TEMP_BRANDS = 'branding/PREFETCH_TEMP_BRANDS';
const REMOVE_TEMP_BRAND = 'branding/REMOVE_TEMP_BRAND';
const FLUSH_LIST = 'branding/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentBrands: createAction(GET_RECENT_BRANDS, BrandsAPI.getPublicBrands),
  prefetchRecentBrands: createAction(PREFETCH_RECENT_BRANDS, BrandsAPI.getPublicBrands),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserBrands: createAction(CLEAR_USER_BRANDS),
  getUserBrands: createAction(
    GET_USER_BRANDS,
    BrandsAPI.getUserBrands,
    (meta: { username: string, tag?: string }) => meta,
  ),
  prefetchUserBrands: createAction(PREFETCH_USER_BRANDS, BrandsAPI.getUserBrands),
  getTrendingBrands: createAction(GET_TRENDING_BRANDS, BrandsAPI.getTrendingBrands),
  prefetchTrendingBrands: createAction(PREFETCH_TRENDING_BRANDS, BrandsAPI.getTrendingBrands),
  getTagBrands: createAction(
    GET_TAG_BRANDS,
    BrandsAPI.getPublicBrandsByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagBrands: createAction(PREFETCH_TAG_BRANDS, BrandsAPI.getPublicBrandsByTag),
  getCategoryBrands: createAction(
    GET_CATEGORY_BRANDS,
    BrandsAPI.getPublicBrandsByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryBrands: createAction(
    PREFETCH_CATEGORY_BRANDS,
    BrandsAPI.getPublicBrandsByCategory,
  ),
  clearCategoryBrands: createAction(CLEAR_CATEGORY_BRANDS),
  clearTagBrands: createAction(CLEAR_TAG_BRANDS),
  getTempBrands: createAction(GET_TEMP_BRANDS, BrandsAPI.getTempBrands),
  prefetchTempBrands: createAction(PREFETCH_TEMP_BRANDS, BrandsAPI.getTempBrands),
  removeTempBrand: createAction(REMOVE_TEMP_BRAND, (brandId: string) => brandId),
  flushList: createAction(FLUSH_LIST),
};

export type BrandItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    branding_name: string,
    short_bio: string,
    thumbnail: ?string,
  },
  meta: any,
};

export type BrandingSet = {
  brands: ?(BrandItem[]),
  prefetched: ?(BrandItem[]),
  end: boolean,
};

export type Branding = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: BrandingSet,
  recent: BrandingSet,
  user: BrandingSet & { currentUsername: ?string, currentTag: ?string },
  tag: BrandingSet & { currentTag: ?string },
  category: BrandingSet & { currentCategory: ?string },
  temp: BrandingSet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type BrandsResponseAction = {
  type: string,
  payload: {
    data: BrandItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempBrandAction = ActionType<typeof actionCreators.removeTempBrand>;

export interface BrandingActionCreators {
  getRecentBrands(cursor: string, category: string, sort: string): any;

  prefetchRecentBrands(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserBrands(payload: BrandsAPI.GetUserBrandsPayload): any;

  prefetchUserBrands(payload: BrandsAPI.GetUserBrandsPayload): any;

  getTrendingBrands(): any;

  prefetchTrendingBrands(offset: number): any;

  getTagBrands(payload: BrandsAPI.GetPublicBrandsByTagPayload): any;

  prefetchTagBrands(payload: BrandsAPI.GetPublicBrandsByTagPayload): any;

  getCategoryBrands(payload: BrandsAPI.GetPublicBrandsByCategoryPayload): any;

  prefetchCategoryBrands(payload: BrandsAPI.GetPublicBrandsByCategoryPayload): any;

  getTempBrands(payload: BrandsAPI.GetTempBrandsPayload): any;

  prefetchTempBrands(payload: BrandsAPI.GetTempBrandsPayload): any;

  clearCategoryBrands(): any;

  clearTagBrands(): any;

  clearUserBrands(): any;

  removeTempBrand(brandId: string): any;

  flushList(): any;
}

const initialBrandingSet = {
  brands: null,
  prefetched: null,
  end: false,
};

const initialState: Branding = {
  trendingMap: {},
  trending: initialBrandingSet,
  recent: initialBrandingSet,
  user: { ...initialBrandingSet, currentUsername: null, currentTag: null },
  tag: { ...initialBrandingSet, currentTag: null },
  category: { ...initialBrandingSet, currentCategory: null },
  temp: initialBrandingSet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { brands, prefetched } = draft[payload];
        if (brands && prefetched) {
          brands.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_BRANDS]: state => {
      return produce(state, draft => {
        draft.user = {
          brands: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
        };
      });
    },
    [CLEAR_TAG_BRANDS]: state => {
      return produce(state, draft => {
        draft.tag = {
          brands: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_CATEGORY_BRANDS]: state => {
      return produce(state, draft => {
        draft.category = {
          brands: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_BRAND]: (state, { payload }: RemoveTempBrandAction) => {
      return produce(state, draft => {
        if (!draft.temp.brands) return;
        draft.temp.brands = draft.temp.brands.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          brands: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          brands: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          brands: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(brand => {
          draft.trendingMap[brand.id] = true;
        });
        draft.trending = {
          end: false,
          brands: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(brand => !state.trendingMap[brand.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(brand => {
          draft.trendingMap[brand.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_BRANDS,
    onPending: (state: Branding) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      return produce(state, draft => {
        draft.user.brands = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_BRANDS,
    onPending: (state: Branding) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      return produce(state, draft => {
        draft.temp.brands = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_BRANDS,
    onSuccess: (state: Branding, action: BrandsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/branding.js